import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines, faPen, faFilePdf, faRotate, faFileInvoiceDollar, faBan, faPaperPlane, faSignature, faFileExcel, faSort, faSortUp, faSortDown, faClone } from '@fortawesome/free-solid-svg-icons';
import DataService from '../../services/DataService';
import QuoteService from '../../services/QuoteService';
import BillService from '../../services/BillService';
import { Quotes, QuoteLines } from '../../utils/Database';
import TimeStamp from '../../utils/TimeStamp';
import QuoteLineModal from './QuoteLineModal';
import CloneQuoteModal from './CloneQuoteModal';
import PDFDocumentService from '../../services/PDFDocumentService';
import DataDocumentService from '../../services/DataDocumentService';
import ExcelQuoteService from '../../services/ExcelQuoteService';
import Tooltip from '../../components/common/Tooltip';

const QuoteList = () => {
    const [quotes, setQuotes] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [quoteStatuses, setQuoteStatuses] = useState({});
    const [selectedQuoteId, setSelectedQuoteId] = useState(null);
    const [isModalActive, setIsModalActive] = useState(false);
    const [isCloneModalActive, setIsCloneModalActive] = useState(false);
    const [cloneSourceQuoteCustomId, setCloneSourceQuoteCustomId] = useState(null);
    const [quotesWithLines, setQuotesWithLines] = useState({});
    const [quotesWithBills, setQuotesWithBills] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const pageSize = 10;

    // Charger les statuts de devis
    const fetchQuoteStatuses = useCallback(async () => {
        try {
            const statuses = await DataService.getTypes('QuoteStatus');
            const statusMap = {};
            statuses.forEach(status => {
                statusMap[status.name] = status.data;
            });
            setQuoteStatuses(statusMap);
        } catch (error) {
            console.error('Erreur lors de la récupération des statuts:', error);
        }
    }, []);

    const fetchQuotes = useCallback(async () => {
        try {
            const [opportunities, pages] = await Promise.all([
                DataService.getAll('opportunities', currentPage, pageSize),
                DataService.getTotalPages('opportunities', pageSize)
            ]);

            const quotesWithDetails = await Promise.all(opportunities.map(async (opportunity) => {
                const account = await DataService.getById('accounts', opportunity.accountId);
                const quotes = await DataService.filter('quotes', {
                    opportunityId: opportunity.id,
                    isPrimary: true
                });
            
                const primaryQuote = quotes[0];
            
                // Vérifier si le devis a des lignes et une facture
                let hasLines = false;
                let hasBill = false;
                let total = 0;
                if (primaryQuote) {
                    const quoteWithDetails = await DataService.getQuoteWithLines(primaryQuote.id);
                    hasLines = quoteWithDetails.lines && quoteWithDetails.lines.length > 0;
                    hasBill = !!quoteWithDetails.bill;
                    setQuotesWithLines(prev => ({
                        ...prev,
                        [primaryQuote.quoteCustomId]: hasLines
                    }));
                    setQuotesWithBills(prev => ({
                        ...prev,
                        [primaryQuote.quoteCustomId]: hasBill
                    }));
                    total = await primaryQuote.getTotal();
                }
            
                return {
                    id: opportunity.id,
                    accountName: account ? account.name : '',
                    opportunityName: opportunity.name,
                    primaryQuote: primaryQuote ? primaryQuote.quoteCustomId : '',
                    status: primaryQuote ? primaryQuote.status : 'N/A',
                    timeStamp: primaryQuote ? primaryQuote.timeStamp : null,
                    total: total
                };
            }));

            setQuotes(quotesWithDetails);
            setTotalPages(pages);
        } catch (error) {
            console.error('Erreur lors de la récupération des devis:', error);
        }
    }, [currentPage]);

    // Fonction pour formater la date
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        // Convertir l'objet JSON TimeStamp en instance TimeStamp
        const timeStampInstance = TimeStamp.fromString(`${timestamp.year}-${timestamp.month}-${timestamp.day}-${timestamp.hour}-${timestamp.minute}`);
        return timeStampInstance.toFormattedDate();
    };

    useEffect(() => {
        fetchQuoteStatuses();
        fetchQuotes();
    }, [fetchQuotes, fetchQuoteStatuses]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleEditLines = async (quoteCustomId) => {
        if (!quoteCustomId) {
            console.error('QuoteCustomId invalide');
            return;
        }
        try {
            // D'abord récupérer le devis par son quoteCustomId
            const quote = await DataService.getQuoteByCustomId(quoteCustomId);
            if (!quote) {
                console.error('Devis non trouvé');
                return;
            }
            // Ensuite utiliser l'ID réel du devis
            setSelectedQuoteId(quote.id);
            setIsModalActive(true);
        } catch (error) {
            console.error('Erreur lors de la récupération du devis:', error);
        }
    };

    const handleCloseModal = () => {
        setIsModalActive(false);
        setSelectedQuoteId(null);
        fetchQuotes();
    };

    const handleCloneQuote = async (quoteCustomId) => {
        try {
            const quote = await DataService.getQuoteByCustomId(quoteCustomId);
            if (!quote) {
                console.error('Devis non trouvé');
                return;
            }
    
            const newQuoteCustomId = QuoteService.createNewVersion(quoteCustomId);
            const newQuote = new Quotes(
                newQuoteCustomId,
                quote.opportunityId,
                TimeStamp.fromDate(new Date()),
                'Draft',
                quote.name,
                true,
                quote.notes,
                quote.delay,
                quote.outOfScope,
                quote.needsFromClient
            );
    
            const savedNewQuote = await newQuote.save();
    
            // Récupérer la hiérarchie complète des lignes
            const quoteLines = await DataService.getQuoteLineHierarchy(quote.id);
            
            // Map pour stocker les correspondances entre anciens et nouveaux IDs
            const idMapping = new Map();
            
            // Fonction récursive pour cloner les lignes en respectant la hiérarchie
            const cloneLineWithChildren = async (line, parentId = null) => {
                const newLine = new QuoteLines(
                    line.description,
                    savedNewQuote.id,
                    parentId, // Utiliser le nouveau parentId
                    line.unitPrice,
                    line.quantity,
                    line.productId,
                    line.position // Copier la position de la ligne originale
                );
                
                const savedLine = await newLine.save();
                idMapping.set(line.id, savedLine.id);
                
                // Cloner récursivement les sous-lignes
                if (line.subLines && line.subLines.length > 0) {
                    for (const subLine of line.subLines) {
                        await cloneLineWithChildren(subLine, savedLine.id);
                    }
                }
            };
            
            // Cloner les lignes de premier niveau et leurs enfants
            for (const line of quoteLines) {
                if (!line.parentLineId) { // Seulement les lignes de premier niveau
                    await cloneLineWithChildren(line);
                }
            }
            
    
            const oldQuotes = await DataService.filter('quotes', {
                opportunityId: quote.opportunityId,
                isPrimary: true
            });
    
            for (const oldQuote of oldQuotes) {
                await DataService.update('quotes', oldQuote.id, { isPrimary: false });
            }
    
            await DataService.update('quotes', savedNewQuote.id, { isPrimary: true });
    
            fetchQuotes();
        } catch (error) {
            console.error('Erreur lors du clonage du devis:', error);
        }
    };

    const openCloneToAccountModal = (quoteCustomId) => {
        setCloneSourceQuoteCustomId(quoteCustomId);
        setIsCloneModalActive(true);
    };

    const closeCloneToAccountModal = () => {
        setIsCloneModalActive(false);
        setCloneSourceQuoteCustomId(null);
        fetchQuotes();
    };
    
    const handleUpdateQuoteStatus = async (quoteCustomId, newStatus) => {
        try {
            const quote = await DataService.getQuoteByCustomId(quoteCustomId);
            if (!quote) {
                console.error('Devis non trouvé');
                return;
            }

            await DataService.update('quotes', quote.id, { ...quote, status: newStatus });
            fetchQuotes(); // Rafraîchir la liste pour mettre à jour l'affichage des boutons
        } catch (error) {
            console.error(`Erreur lors de la mise à jour du statut du devis à ${newStatus}:`, error);
        }
    };

    const handleGenerateBill = async (quoteCustomId) => {
        try {
            const quote = await DataService.getQuoteByCustomId(quoteCustomId);
            if (!quote) {
                console.error('Devis non trouvé');
                return;
            }

            await BillService.createBillFromQuote(quote);
            fetchQuotes(); // Rafraîchir la liste pour mettre à jour l'affichage des boutons
        } catch (error) {
            console.error('Erreur lors de la génération de la facture:', error);
        }
    };

    const handleGeneratePDF = async (quoteCustomId) => {
        try {
            const dataService = new DataDocumentService();
            const newData = await dataService.getQuoteDocumentData(quoteCustomId);
            // Générer le PDF
            const pdfService = new PDFDocumentService();

            pdfService.generatePDF(newData);
        } catch (error) {
            console.error('Erreur lors de la génération du PDF:', error);
        }
    };

    const handleGenerateExcel = async (quoteCustomId) => {
        try {
            const excelService = new ExcelQuoteService();
            await excelService.generateQuoteExcel(quoteCustomId);
        } catch (error) {
            console.error('Erreur lors de la génération du fichier Excel:', error);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedQuotes = () => {
        if (!sortConfig.key) return quotes;

        return [...quotes].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'account':
                    aValue = a.accountName;
                    bValue = b.accountName;
                    break;
                case 'project':
                    aValue = a.opportunityName;
                    bValue = b.opportunityName;
                    break;
                case 'quote':
                    aValue = a.primaryQuote;
                    bValue = b.primaryQuote;
                    break;
                case 'date':
                    // Convertir les TimeStamps en dates pour la comparaison
                    aValue = a.timeStamp ? new Date(
                        parseInt(a.timeStamp.year),
                        parseInt(a.timeStamp.month) - 1,
                        parseInt(a.timeStamp.day)
                    ) : new Date(0);
                    bValue = b.timeStamp ? new Date(
                        parseInt(b.timeStamp.year),
                        parseInt(b.timeStamp.month) - 1,
                        parseInt(b.timeStamp.day)
                    ) : new Date(0);
                    break;
                case 'total':
                    aValue = a.total;
                    bValue = b.total;
                    break;
                case 'status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return faSort;
        }
        return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
    };

    return (
        <>
            <div className="panel">
                <div className="panel-heading">
                    <div className="panel-header-content">
                        <span className="icon-text">
                            <span className="icon">
                                <FontAwesomeIcon icon={faFileLines} />
                            </span>
                            <span> Devis</span>
                        </span>
                        <div className="panel-header-actions">
                            <div className="buttons">
                                <button
                                    className="button is-small"
                                    onClick={() => handlePageChange(0)}
                                    disabled={currentPage === 0}
                                >
                                    <span className="icon is-small">««</span>
                                </button>
                                <button
                                    className="button is-small"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 0}
                                >
                                    <span className="icon is-small">«</span>
                                </button>
                                <button
                                    className="button is-small"
                                    title={`Page ${currentPage + 1} sur ${totalPages}`}
                                >
                                    <span className="icon is-small">{currentPage + 1}</span>
                                </button>
                                <button
                                    className="button is-small"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages - 1}
                                >
                                    <span className="icon is-small">»</span>
                                </button>
                                <button
                                    className="button is-small"
                                    onClick={() => handlePageChange(totalPages - 1)}
                                    disabled={currentPage === totalPages - 1}
                                >
                                    <span className="icon is-small">»»</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="panel-block">
                    <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', width: '100%' }}>
                        <table className="table is-fullwidth is-striped is-hoverable mb-0">
                            <thead className="modal-table-header">
                                <tr>
                                    <th>
                                        <button 
                                            className="button table-sort-button"
                                            onClick={() => handleSort('account')}
                                        >
                                            Compte <FontAwesomeIcon icon={getSortIcon('account')} className="ml-1" />
                                        </button>
                                    </th>
                                    <th>
                                        <button 
                                            className="button table-sort-button"
                                            onClick={() => handleSort('project')}
                                        >
                                            Projet <FontAwesomeIcon icon={getSortIcon('project')} className="ml-1" />
                                        </button>
                                    </th>
                                    <th>
                                        <button 
                                            className="button table-sort-button"
                                            onClick={() => handleSort('quote')}
                                        >
                                            Devis <FontAwesomeIcon icon={getSortIcon('quote')} className="ml-1" />
                                        </button>
                                    </th>
                                    <th>
                                        <button 
                                            className="button table-sort-button"
                                            onClick={() => handleSort('date')}
                                        >
                                            Date <FontAwesomeIcon icon={getSortIcon('date')} className="ml-1" />
                                        </button>
                                    </th>
                                    <th>
                                        <button 
                                            className="button table-sort-button"
                                            onClick={() => handleSort('total')}
                                        >
                                            Montant Total <FontAwesomeIcon icon={getSortIcon('total')} className="ml-1" />
                                        </button>
                                    </th>
                                    <th>
                                        <button 
                                            className="button table-sort-button"
                                            onClick={() => handleSort('status')}
                                        >
                                            Statut <FontAwesomeIcon icon={getSortIcon('status')} className="ml-1" />
                                        </button>
                                    </th>
                                    <th className="has-text-right">
                                        <Tooltip content={
                                            <div>
                                                <strong>Workflow des actions par statut</strong>
                                                <div className="workflow-columns">
                                                    <div className="workflow-column">
                                                        <h4>Brouillon</h4>
                                                        <ul>
                                                            <li>• Éditer les lignes</li>
                                                            <li>• Cloner (autre compte)</li>
                                                            <li>• Envoyer (si montant &gt; 0)</li>
                                                            <li>• Annuler</li>
                                                            <li>• Générer PDF (si lignes)</li>
                                                        </ul>
                                                    </div>
                                                    <div className="workflow-column">
                                                        <h4>Envoyé</h4>
                                                        <ul>
                                                            <li>• Cloner (nouvelle version)</li>
                                                            <li>• Cloner (autre compte)</li>
                                                            <li>• Signer</li>
                                                            <li>• Annuler</li>
                                                            <li>• Générer PDF (si lignes)</li>
                                                        </ul>
                                                    </div>
                                                    <div className="workflow-column">
                                                        <h4>Signé</h4>
                                                        <ul>
                                                            <li>• Cloner (autre compte)</li>
                                                            <li>• Générer facture</li>
                                                            <li>• Export Excel (si facture)</li>
                                                            <li>• Générer PDF (si lignes)</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        }>
                                            Actions
                                        </Tooltip>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {getSortedQuotes().map(quote => (
                                    <tr key={quote.id}>
                                        <td>{quote.accountName}</td>
                                        <td>{quote.opportunityName}</td>
                                        <td>{quote.primaryQuote}</td>
                                        <td>{formatDate(quote.timeStamp)}</td>
                                        <td style={{ textAlign: 'right'}}>{quote.total.toFixed(2)} €</td>
                                        <td className={
                                            quote.status === 'Signed' ? 'has-text-success' :
                                            quote.status === 'Canceled' ? 'has-text-danger' :
                                            ''
                                        }>
                                            {quoteStatuses[quote.status] || quote.status}
                                        </td>
                                        <td>
                                            <div className="table-actions-container">
                                                <div className="buttons are-small">
                                                    {quote.status === 'Draft' && (
                                                        <>
                                                            <button
                                                                className="button is-small table-action-button"
                                                                onClick={() => handleEditLines(quote.primaryQuote)}
                                                                title="Éditer les lignes"
                                                            >
                                                                <span className="icon icon-primary">
                                                                    <FontAwesomeIcon icon={faPen} />
                                                                </span>
                                                            </button>
                                                            <button
                                                                className="button is-small table-action-button"
                                                                onClick={() => openCloneToAccountModal(quote.primaryQuote)}
                                                                title="Cloner vers un autre client"
                                                            >
                                                                <span className="icon is-small icon-primary">
                                                                    <FontAwesomeIcon icon={faClone} />
                                                                </span>
                                                            </button>
                                                        </>
                                                        )}
                                                        {quote.status === 'Draft'  && quote.total > 0.01 && (
                                                           <>
                                                            <button
                                                                className="button is-small table-action-button"
                                                                onClick={() => handleUpdateQuoteStatus(quote.primaryQuote, 'Sent')}
                                                                title="Envoyer le devis"
                                                            >
                                                                <span className="icon is-small icon-primary">
                                                                    <FontAwesomeIcon icon={faPaperPlane} />
                                                                </span>
                                                            </button>
                                                        </>
                                                    )}
                                                    {quote.status === 'Sent' && (
                                                        <>
                                                            <button
                                                                className="button is-small table-action-button"
                                                                onClick={() => handleCloneQuote(quote.primaryQuote)}
                                                                title="Cloner le devis (même client)"
                                                            >
                                                                <span className="icon is-small icon-primary">
                                                                    <FontAwesomeIcon icon={faRotate} />
                                                                </span>
                                                            </button>

                                                            <button
                                                                className="button is-small table-action-button"
                                                                onClick={() => openCloneToAccountModal(quote.primaryQuote)}
                                                                title="Cloner vers un autre client"
                                                            >
                                                                <span className="icon is-small icon-primary">
                                                                    <FontAwesomeIcon icon={faClone} />
                                                                </span>
                                                            </button>

                                                            <button
                                                                className="button is-small table-action-button"
                                                                onClick={() => handleUpdateQuoteStatus(quote.primaryQuote, 'Signed')}
                                                                title="Signer le devis"
                                                            >
                                                                <span className="icon is-small icon-primary">
                                                                    <FontAwesomeIcon icon={faSignature} />
                                                                </span>
                                                            </button>
                                                        </>
                                                    )}
                                                    {(quote.status === 'Draft' || quote.status === 'Sent') && (
                                                        <button
                                                            className="button is-small table-action-button"
                                                            onClick={() => handleUpdateQuoteStatus(quote.primaryQuote, 'Canceled')}
                                                            title="Annuler le devis"
                                                        >
                                                            <span className="icon is-small icon-danger">
                                                                <FontAwesomeIcon icon={faBan} />
                                                            </span>
                                                        </button>
                                                    )}
                                                    {(quote.status === 'Signed') && (
                                                            <button
                                                                className="button is-small table-action-button"
                                                                onClick={() => openCloneToAccountModal(quote.primaryQuote)}
                                                                title="Cloner vers un autre client"
                                                            >
                                                                <span className="icon is-small icon-primary">
                                                                    <FontAwesomeIcon icon={faClone} />
                                                                </span>
                                                            </button>
                                                    )}
                                                    {quotesWithLines[quote.primaryQuote] && (
                                                        <button
                                                            className="button is-small table-action-button"
                                                            onClick={() => handleGeneratePDF(quote.primaryQuote)}
                                                            title="Générer le PDF"
                                                        >
                                                            <span className="icon is-small icon-success">
                                                                <FontAwesomeIcon icon={faFilePdf} />
                                                            </span>
                                                        </button>
                                                    )}
                                                    {quote.status === 'Signed' && quotesWithBills[quote.primaryQuote] && (
                                                        <button
                                                            className="button is-small table-action-button"
                                                            onClick={() => handleGenerateExcel(quote.primaryQuote)}
                                                            title="Exporter les lignes en Excel"
                                                        >
                                                            <span className="icon is-small icon-success">
                                                                <FontAwesomeIcon icon={faFileExcel} />
                                                            </span>
                                                        </button>
                                                    )}
                                                    {quote.status === 'Signed' && !quotesWithBills[quote.primaryQuote] && (
                                                        <button
                                                            className="button is-small table-action-button"
                                                            onClick={() => handleGenerateBill(quote.primaryQuote)}
                                                            title="Générer la facture"
                                                        >
                                                            <span className="icon is-small icon-primary">
                                                                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                                            </span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="panel-footer"></div>
            </div>

            <QuoteLineModal
                isActive={isModalActive}
                onClose={handleCloseModal}
                quoteId={selectedQuoteId}
            />

            <CloneQuoteModal
                isActive={isCloneModalActive}
                onClose={closeCloneToAccountModal}
                sourceQuoteCustomId={cloneSourceQuoteCustomId}
                onCloned={() => {
                    // refresh and optionally navigate - currently refresh
                    fetchQuotes();
                }}
            />
        </>
    );
};

export default QuoteList;
